"use client";

import { Sparkline } from "@ui/components/sparkline";
import { cn } from "@ui/lib";
import { CHART_SERIES, DELTA_TONE_CLASSES } from "@ui/lib/chart-palette";
import { ArrowRightIcon } from "lucide-react";

/**
 * Before → after for position-style metrics where the delta is the wrong
 * mental model. A rank that goes #28 → #3 improved, but "-25" and a falling
 * line read as decline to anyone who thinks in rankings. So: lead with the
 * two states themselves, badge the tone in words (an arrow would be
 * ambiguous — the raw number fell while the ranking rose), and draw the
 * optional trend so that up always means better regardless of the metric's
 * raw sign. Graduated from the design lab (which re-exports this
 * component).
 */
export function BeforeAfter({
	label,
	before,
	after,
	tone = "positive",
	caption,
	series,
	lowerIsBetter,
	className,
}: {
	label: string;
	before: string;
	after: string;
	/** Whether the move from before to after is good, bad, or neither. */
	tone?: "positive" | "negative" | "neutral";
	caption?: string;
	/** Raw values over time; drawn so that up always means better. */
	series?: number[];
	/** True for position-style metrics (ranks) where a lower raw value is better. */
	lowerIsBetter?: boolean;
	className?: string;
}) {
	return (
		<div className={className}>
			<div className="flex items-center justify-between gap-2">
				<p className="text-xs font-semibold text-muted-foreground">
					{label}
				</p>
				<span
					className={cn(
						"rounded-full border px-2 py-0.5 text-xs font-semibold",
						DELTA_TONE_CLASSES[tone],
					)}
				>
					{tone === "positive"
						? "Improved"
						: tone === "negative"
							? "Worsened"
							: "No change"}
				</span>
			</div>
			<p className="mt-1.5 flex items-baseline gap-2">
				<span className="text-lg font-semibold text-muted-foreground">
					{before}
				</span>
				<ArrowRightIcon
					className="size-4 self-center text-muted-foreground"
					aria-hidden
				/>
				<span className="font-mono text-2xl font-extrabold tabular-nums">
					{after}
				</span>
			</p>
			{caption && (
				<p className="mt-1 text-xs text-muted-foreground">{caption}</p>
			)}
			{series && series.length > 1 && (
				<div className="mt-2">
					<Sparkline
						data={lowerIsBetter ? series.map((v) => -v) : series}
						color={CHART_SERIES[0]}
						height={48}
					/>
					<p className="mt-0.5 text-right text-[11px] text-muted-foreground">
						up is better
					</p>
				</div>
			)}
		</div>
	);
}
